/**
 * The month grid of the personal tab: one row per Redmine project ("line"),
 * one column per working day, logged values solid and the recommendation as
 * grey ghosts. Below 640 px it hands over to DayCardList.
 */
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import type { ImputacionLine, ImputacionMonth } from '../../../types/imputacion'
import {
    CellInput,
    Chip,
    DayCardList,
    DayChip,
    type GridProps,
    canFillDay,
    cellDomId,
    dayDomId,
    dayTotalInfo,
} from './DayCardList'
import {
    type Draft,
    dayTotal,
    ddmm,
    dayLabel,
    draftIssues,
    fmtH,
    fmtNum,
    orderedLines,
    redmineDayUrl,
    visibleDays,
} from './imputacionLogic'

const NARROW_QUERY = '(max-width: 639px)'

/** True below 640 px. jsdom and old browsers have no matchMedia: desktop then. */
export function useIsNarrow(): boolean {
    const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches
    const [narrow, setNarrow] = useState(get)
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
        const mq = window.matchMedia(NARROW_QUERY)
        const onChange = () => setNarrow(mq.matches)
        mq.addEventListener?.('change', onChange)
        return () => mq.removeEventListener?.('change', onChange)
    }, [])
    return narrow
}

const Wrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const Th = styled.th<{ $highlight?: boolean; $weekend?: boolean }>`
  padding: 4px;
  min-width: 58px;
  text-align: center;
  vertical-align: top;
  font-weight: 600;
  background: ${({ $weekend, theme }) => ($weekend ? '#F5F5F5' : theme.colors.secondary)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  ${({ $highlight }) => $highlight && css`box-shadow: inset 0 0 0 3px #FFB300;`}
`

const StickyTh = styled.th`
  position: sticky;
  left: 0;
  z-index: 2;
  max-width: 180px;
  min-width: 140px;
  padding: 6px 8px;
  text-align: left;
  background: ${({ theme }) => theme.colors.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
`

const StickyTd = styled.td<{ $muted?: boolean }>`
  position: sticky;
  left: 0;
  z-index: 1;
  max-width: 180px;
  min-width: 140px;
  padding: 6px 8px;
  text-align: left;
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ $muted, theme }) => ($muted ? theme.colors.textLight : theme.colors.text)};
`

const Td = styled.td<{ $weekend?: boolean }>`
  padding: 3px;
  text-align: center;
  vertical-align: top;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $weekend }) => ($weekend ? '#FAFAFA' : 'white')};
`

const LineName = styled.div`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const LineMeta = styled.div`
  font-size: 0.68rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

const DayExpected = styled.div`
  font-size: 0.68rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textMuted};
`

const FillLink = styled.button`
  display: block;
  margin: 2px auto 0;
  border: none;
  background: none;
  padding: 0;
  color: #1565C0;
  font-size: 0.68rem;
  text-decoration: underline;
  cursor: pointer;
`

const TotalCell = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
`

const Issues = styled.ul`
  margin: ${({ theme }) => theme.spacing.sm} 0 0;
  padding-left: 1.1rem;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: #C62828;

  a {
    margin-left: 6px;
  }
`

const Note = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

/** "Imputado 147 h · Plan 53 h ▲94" — a neutral comparison, never a "Faltan" on a row. */
function lineMeta(line: ImputacionLine): string {
    const s = line.summary
    if (line.kind === 'needs_choice') return `${fmtH(s.plan_month)} por repartir`
    const diff = s.logged_month - s.plan_month
    const arrow = Math.abs(diff) < 0.01 ? '' : diff > 0 ? ` ▲${fmtNum(diff)}` : ` ▼${fmtNum(-diff)}`
    if (s.plan_month <= 0) return `Imputado ${fmtH(s.logged_month)}`
    return `Imputado ${fmtH(s.logged_month)} · Plan ${fmtH(s.plan_month)}${arrow}`
}

function IssueList({ month, draft }: { month: ImputacionMonth; draft: Draft }) {
    const issues = draftIssues(month, draft)
    if (!issues.length) return null
    return (
        <Issues aria-live="polite">
            {issues.map(({ lineKey, date, issue }) => {
                const line = month.lines.find((l) => l.key === lineKey)
                const url = issue.kind === 'floor' ? redmineDayUrl(month.redmine_url, date) : null
                return (
                    <li key={`${lineKey}|${date}`}>
                        {ddmm(date)} · {line?.label}: {issue.message}
                        {url && (
                            <a href={url} target="_blank" rel="noreferrer">
                                Abrir este día en Redmine ↗
                            </a>
                        )}
                    </li>
                )
            })}
        </Issues>
    )
}

export function ImputacionGrid(props: GridProps) {
    const { month, draft, readOnly, onCellChange, onFillDay, results, conflictDays, highlightDay, locked = false } = props
    const narrow = useIsNarrow()

    useEffect(() => {
        if (!highlightDay) return
        document.getElementById(dayDomId(highlightDay))?.scrollIntoView?.({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }, [highlightDay])

    if (narrow) {
        return (
            <>
                <DayCardList {...props} />
                {!readOnly && <IssueList month={month} draft={draft} />}
            </>
        )
    }

    const lines = orderedLines(month)
    const days = visibleDays(month)

    return (
        <>
            <Wrapper>
                <Table>
                    <thead>
                        <tr>
                            <StickyTh scope="col">Proyecto</StickyTh>
                            {days.map((day) => (
                                <Th
                                    key={day.date}
                                    id={dayDomId(day.date)}
                                    scope="col"
                                    $highlight={highlightDay === day.date}
                                    $weekend={day.kind !== 'working'}
                                >
                                    <div>{dayLabel(day.date)}</div>
                                    <DayExpected>{day.kind === 'working' ? fmtH(day.expected_hours) : 'no laborable'}</DayExpected>
                                    <DayChip day={day} conflict={Boolean(conflictDays?.has(day.date))} />
                                    {!locked && canFillDay(month, day, readOnly) && (
                                        <FillLink type="button" onClick={() => onFillDay(day.date)}>
                                            Completar
                                        </FillLink>
                                    )}
                                </Th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line) => (
                            <tr key={line.key} data-line={line.key}>
                                <StickyTd $muted={!line.loggable && line.kind === 'needs_choice'} title={line.label}>
                                    <LineName>{line.label}</LineName>
                                    <LineMeta>{lineMeta(line)}</LineMeta>
                                </StickyTd>
                                {days.map((day) => (
                                    <Td key={day.date} $weekend={day.kind !== 'working'}>
                                        <CellInput
                                            month={month}
                                            line={line}
                                            day={day}
                                            draft={draft}
                                            readOnly={readOnly}
                                            onChange={onCellChange}
                                            result={results[`${line.key}|${day.date}`]}
                                            locked={locked}
                                        />
                                    </Td>
                                ))}
                            </tr>
                        ))}
                        <tr>
                            <StickyTd>
                                <LineName>Total día</LineName>
                            </StickyTd>
                            {days.map((day) => {
                                const total = dayTotal(month, day.date, draft)
                                if (day.state === 'future' && total === 0) return <Td key={day.date} $weekend={day.kind !== 'working'} />
                                const info =
                                    day.expected_hours > 0
                                        ? dayTotalInfo(total, day.expected_hours)
                                        : { text: total > 0 ? fmtNum(total) : '', tone: 'muted' as const }
                                return (
                                    <Td key={day.date} $weekend={day.kind !== 'working'} title={info.title}>
                                        <TotalCell>
                                            {fmtNum(total)}{' '}
                                            {info.text && day.expected_hours > 0 && <Chip $tone={info.tone}>{info.text}</Chip>}
                                        </TotalCell>
                                    </Td>
                                )
                            })}
                        </tr>
                    </tbody>
                </Table>
            </Wrapper>
            {!readOnly && <IssueList month={month} draft={draft} />}
            <Note>No se muestran horas en proyectos archivados.</Note>
        </>
    )
}

export { cellDomId }
